import { type VariantProps, cva } from 'class-variance-authority'

export { default as Button } from './Button.vue'

export const buttonVariants = cva(
  // Common classes applied to all buttons
  `
    inline-flex items-center gap-2 justify-center whitespace-nowrap
    rounded-md font-semibold disabled:cursor-not-allowed
  `,
  {
    variants: {
      hierarchy: {
        primary: `
          bg-brand-700 text-base-white
          hover:bg-brand-800 :hover:text-base-white
          focus:ring-3 focus:ring-brand
          disabled:bg-gray-warm-100 disabled:text-gray-warm-400 disabled:border-gray-warm-200
        `,
        'secondary-gray': `
          bg-base-white text-gray-warm-700 border-gray-warm-300 border
          hover:bg-gray-warm-50 :hover:text-gray-warm-800
          focus:ring-3 focus:ring-gray
          disabled:bg-base-white disabled:text-gray-warm-400 disabled:border-gray-warm-200
        `,
        'secondary-color': `
          bg-base-white text-brand-700 border-brand-700 border
          hover:bg-gray-warm-50 :hover:text-brand-700
          focus:ring-3 focus:ring-brand
          disabled:bg-base-white disabled:text-gray-warm-400 disabled:border-gray-warm-200
        `,
        'secondary-destructive': `
          bg-base-white text-error-700 border-error-700 border
          hover:bg-error-25 :hover:text-error-700
          focus:ring-3 focus:ring-error
          disabled:bg-base-white disabled:text-gray-warm-400 disabled:border-gray-warm-200
        `,
        'tertiary-color': `
          bg-base-white text-gray-warm-600 border-warning-300 border
          hover:warning-300 :hover:text-gray-warm-50
          focus:ring-3 focus:ring-warning
          disabled:bg-base-white disabled:text-gray-warm-400 disabled:border-gray-warm-200
        `,
        'link-gray': `
          text-gray-warm-600
          hover:underline
          disabled:text-gray-warm-400
        `,

        'link-color': `
          text-brand-700
          hover:underline
          disabled:text-gray-warm-400
        `,
        'link-destructive': `
          text-error-700
          hover:underline
          focus-visible:bg-error-50 focus-visible:ring-3 focus-visible:ring-error
          disabled:bg-transparent disabled:text-gray-warm-400
        `,
        'pagination-button': `
          bg-base-white text-gray-warm-600
          hover:bg-gray-warm-50 hover:text-gray-warm-800
          focus:ring-3 focus:ring-gray
          disabled:cursor-default disabled:bg-brand-100 disabled:text-brand-700
        `,
        destructive: `
          bg-error-600 text-base-white border-error-600 border
          hover:bg-error-700 :hover:text-base-white
          focus:ring-3 focus:ring-error
          disabled:bg-gray-warm-100 disabled:text-gray-warm-400 disabled:border-gray-warm-200
        `
      },
      size: {
        sm: 'px-[12px] py-[8px] text-sm',
        md: 'px-[14px] py-[10px] text-sm',
        lg: 'px-[16px] py-[10px] text-md',
        xl: 'px-[18px] py-[12px] text-md',
        '2xl': 'px-[22px] py-[16px] text-lg'
      }
    },
    defaultVariants: {
      hierarchy: 'primary',
      size: 'md'
    }
  }
)

export const buttonIconVariants = cva(
  // Common classes applied to all buttons
  `
    pr-3
  `,
  {
    variants: {
      size: {
        sm: 'px-[12px] py-[8px] text-sm',
        md: 'px-[14px] py-[10px] text-sm',
        lg: 'px-[16px] py-[10px] text-md',
        xl: 'px-[18px] py-[12px] text-md',
        '2xl': 'px-[22px] py-[16px] text-lg'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
export type ButtonIconVariants = VariantProps<typeof buttonIconVariants>
